import { cloneElement, useId, useState, type FocusEvent, type MouseEvent, type ReactElement, type ReactNode } from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserSettingsContext } from "@/contexts/UserSettingsContext";
import { cn } from "@/lib/utils";

type Side = "top" | "right" | "bottom" | "left";

// Radix opens a tooltip whenever its trigger takes focus, including when a
// menu or dialog hands focus back straight after a click. Then the name pops
// up and stays although nobody is hovering. The browser's :focus-visible says
// whether focus came from the keyboard; only then does focus open it.
//
// Escape is a key press, so focus a popup hands back after Escape counts as
// keyboard focus too. A control that opens a popup (it carries aria-expanded)
// and takes focus from nowhere (the popup it came from is gone) is being handed
// focus back, unless the person just pressed Tab: then they moved to it, for
// example with the first Tab after the page loads, and on an icon-only control
// the name is its only label, so it must show.
let lastKeyWasTab = false;
if (typeof document !== "undefined") {
  document.addEventListener("keydown", (event) => {
    lastKeyWasTab = event.key === "Tab" && !event.altKey && !event.ctrlKey && !event.metaKey;
  }, true);
  document.addEventListener("pointerdown", () => {
    lastKeyWasTab = false;
  }, true);
}

function isFocusHandedBack(event: FocusEvent<HTMLElement>): boolean {
  return event.currentTarget.hasAttribute("aria-expanded") && event.relatedTarget === null && !lastKeyWasTab;
}

function skipFocusAfterClick(event: FocusEvent<HTMLElement>) {
  const target = event.currentTarget;
  if (target !== document.activeElement) return;
  if (isFocusHandedBack(event)) {
    event.preventDefault();
    return;
  }
  try {
    if (!target.matches(":focus-visible")) event.preventDefault();
  } catch {
    // A browser without :focus-visible keeps Radix's own behaviour.
  }
}

function isDisabled(control: ReactElement): boolean {
  return Boolean((control.props as { disabled?: unknown }).disabled);
}

interface AlwaysShownTooltipProps {
  content: ReactNode;
  /** Why the control is greyed out. Only used while the control is disabled. */
  reason?: string;
  side: Side;
  delayDuration: number;
  wrapperClassName?: string;
  children: ReactElement;
}

/**
 * A tooltip that shows even with help tips off, on a control that may be
 * greyed out. A disabled control takes no pointer or focus events, so it sits
 * in a wrapper that does. With a reason, the wrapper also takes keyboard focus
 * and opens on a tap (touch screens never hover), and the reason is written
 * into the page for screen readers rather than living only in the tooltip.
 */
function AlwaysShownTooltip({ content, reason, side, delayDuration, wrapperClassName, children }: AlwaysShownTooltipProps) {
  const [open, setOpen] = useState(false);
  const reasonId = useId();
  const disabled = isDisabled(children);
  const withReason = disabled && Boolean(reason);

  const describedBy = [(children.props as { "aria-describedby"?: string })["aria-describedby"], reasonId]
    .filter(Boolean)
    .join(" ");
  // The wrapper exists only while the control is greyed out. A wrapper kept
  // while usable (with no box) swallowed the gap that space-x and space-y
  // give the control, so buttons touched their neighbours.
  const trigger = disabled ? (
    <span className={cn("inline-flex", wrapperClassName)} tabIndex={withReason ? 0 : undefined}>
      {withReason ? cloneElement(children, { "aria-describedby": describedBy }) : children}
      {withReason && <span id={reasonId} className="sr-only">{reason}</span>}
    </span>
  ) : children;

  // Radix closes a tooltip on click. A tap on a greyed-out control should
  // open it instead; preventDefault stops Radix's close handler running.
  const openOnTap = withReason
    ? (event: MouseEvent) => {
        event.preventDefault();
        setOpen(true);
      }
    : undefined;

  return (
    <TooltipProvider>
      <Tooltip alwaysShow delayDuration={delayDuration} open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild onClick={openOnTap} onFocus={skipFocusAfterClick}>{trigger}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-xs">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface HoverLabelProps {
  /** The control's name. Always shown: on an icon-only control it is the only visible name. */
  label: string;
  /** One plain sentence on what it does. Hidden when the user switches help tips off. */
  hint?: string;
  /** Why the control is greyed out. Shown only while it is disabled, even with help tips off, in place of the hint. */
  reason?: string;
  side?: Side;
  /** A single element that takes a ref and hover events, such as a button or a Radix trigger. */
  children: ReactElement;
}

/**
 * Shows a control's name on hover and keyboard focus, with an optional
 * one-line explanation under it. Use it for icon-only controls; `IconButton`
 * already wraps itself in one. For a control whose name is already on screen,
 * use `HoverHint`. A greyed-out control still shows its name, and its reason
 * when one is given.
 *
 * Each carries its own provider, as `InfoTooltip` does, so they work in any
 * tree, including a dialog portal or a test render without the app shell.
 */
export function HoverLabel({ label, hint, reason, side = "top", children }: HoverLabelProps) {
  const { settings } = useUserSettingsContext();
  const shownReason = isDisabled(children) ? reason : undefined;
  const detail = shownReason || (settings.tooltipsEnabled ? hint : undefined);

  return (
    <AlwaysShownTooltip
      reason={shownReason}
      side={side}
      delayDuration={300}
      content={(
        <>
          <p className="text-xs font-medium">{label}</p>
          {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
        </>
      )}
    >
      {children}
    </AlwaysShownTooltip>
  );
}

interface DisabledReasonProps {
  /** Why the control is greyed out, in one plain sentence. Shown only while the control is disabled. */
  reason?: string | null;
  side?: Side;
  /** Layout classes for the wrapper while greyed out, such as "w-full" when the button fills its row. */
  className?: string;
  /** The control itself, carrying its own `disabled` prop. */
  children: ReactElement;
}

/**
 * Says why a greyed-out control cannot be used yet ("Add a website first").
 * Shown on hover, keyboard focus and tap, and to screen readers, even with
 * help tips off. While the control is usable it renders it untouched, so a
 * control that keeps its own state across greying out (an open dialog) must
 * own its reason itself. For an icon-only button, pass `disabledReason` to
 * `IconButton` instead.
 */
export function DisabledReason({ reason, side = "top", className, children }: DisabledReasonProps) {
  if (!reason || !isDisabled(children)) return children;

  return (
    <AlwaysShownTooltip
      reason={reason}
      side={side}
      delayDuration={200}
      wrapperClassName={className}
      content={reason}
    >
      {children}
    </AlwaysShownTooltip>
  );
}

interface HoverHintProps {
  /** One plain sentence on what the control does. */
  hint: string;
  side?: Side;
  /** Wait before showing, so moving the mouse past a row of controls stays quiet. */
  delayDuration?: number;
  children: ReactElement;
}

/**
 * An explanation for a control whose name is already on screen. Pure help, so
 * it follows the help tips setting and shows nothing when tips are off.
 */
export function HoverHint({ hint, side = "top", delayDuration = 500, children }: HoverHintProps) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={delayDuration}>
        <TooltipTrigger asChild onFocus={skipFocusAfterClick}>{children}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-xs">
          {hint}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
